/**
 * Default legal page templates.
 *
 * These are COPIED into each project's legalPages when the user clicks "Add".
 * After that the project owns its own copy — edits are isolated per-account.
 *
 * Placeholders:
 *   {{COMPANY}}  – replaced with the project name at copy time
 *   {{DATE}}     – replaced with the current date at copy time
 *   {{EMAIL}}    – replaced with "support@<first custom domain>" or a fallback
 */

// ── Terms & Conditions ──────────────────────────────────────────────────

export const TERMS_AND_CONDITIONS = `
<h2>Terms &amp; Conditions</h2>
<p><strong>Effective date:</strong> {{DATE}}</p>
<p>Welcome to <strong>{{COMPANY}}</strong>. By accessing or using our website, mobile application, or any service we offer (collectively, the &ldquo;Service&rdquo;), you agree to be bound by these Terms &amp; Conditions (&ldquo;Terms&rdquo;). If you do not agree, please do not use the Service.</p>

<h3>1. Definitions</h3>
<p>&ldquo;We,&rdquo; &ldquo;us,&rdquo; and &ldquo;our&rdquo; refer to <strong>{{COMPANY}}</strong>. &ldquo;You&rdquo; and &ldquo;your&rdquo; refer to the individual or entity accessing the Service. &ldquo;Content&rdquo; means text, images, videos, data, and any other material on the Service.</p>

<h3>2. Eligibility</h3>
<p>You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Service. By using it, you represent that you meet this requirement.</p>

<h3>3. Account Registration</h3>
<p>Some features may require an account. You agree to provide accurate, current, and complete information and to keep it updated. You are responsible for all activity under your account and for maintaining the confidentiality of your credentials.</p>

<h3>4. Acceptable Use</h3>
<p>You agree not to:</p>
<ul>
  <li>Violate any applicable law or regulation;</li>
  <li>Infringe upon the intellectual property or privacy rights of others;</li>
  <li>Transmit malware, spam, or any harmful code;</li>
  <li>Attempt to gain unauthorised access to any part of the Service;</li>
  <li>Use the Service for fraudulent, deceptive, or illegal purposes;</li>
  <li>Interfere with or disrupt the integrity or performance of the Service.</li>
</ul>

<h3>5. Intellectual Property</h3>
<p>All content, trademarks, logos, and software on the Service are the property of <strong>{{COMPANY}}</strong> or its licensors and are protected by applicable intellectual property laws worldwide. You may not copy, reproduce, distribute, or create derivative works without our express written consent.</p>

<h3>6. User-Generated Content</h3>
<p>If you submit or post content through the Service, you grant us a non-exclusive, worldwide, royalty-free licence to use, reproduce, modify, and display it in connection with the Service. You represent that you own or have the rights to grant this licence.</p>

<h3>7. Products &amp; Services</h3>
<p>Where we sell products or services, all descriptions, images, and pricing are provided as accurately as possible but may contain errors. We reserve the right to correct errors, update information, or cancel orders at any time. Prices are subject to change without notice and may be listed in local currency.</p>

<h3>8. Payment Terms</h3>
<p>Payments are processed through third-party payment gateways. By making a purchase you agree to the payment processor's terms. We do not store complete payment card details on our servers.</p>

<h3>9. Shipping &amp; Delivery</h3>
<p>Estimated delivery times are provided for guidance only. We are not liable for delays caused by carriers, customs, or events outside our reasonable control. Risk of loss passes to you upon delivery to the carrier.</p>

<h3>10. Cancellations &amp; Refunds</h3>
<p>Cancellation and refund rights are governed by our Return &amp; Refund Policy (if applicable) and applicable consumer protection laws in your jurisdiction.</p>

<h3>11. Disclaimer of Warranties</h3>
<p>The Service is provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; without warranties of any kind, whether express, implied, or statutory, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Service will be uninterrupted, error-free, or secure.</p>

<h3>12. Limitation of Liability</h3>
<p>To the maximum extent permitted by applicable law, <strong>{{COMPANY}}</strong> and its directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, goodwill, or other intangible losses arising from your use of the Service.</p>

<h3>13. Indemnification</h3>
<p>You agree to indemnify, defend, and hold harmless <strong>{{COMPANY}}</strong> from any claims, losses, damages, liabilities, and expenses (including legal fees) arising from your use of the Service, your violation of these Terms, or your violation of any rights of a third party.</p>

<h3>14. Third-Party Links</h3>
<p>The Service may contain links to third-party websites or services not owned or controlled by us. We assume no responsibility for the content, privacy policies, or practices of any third-party site.</p>

<h3>15. Privacy</h3>
<p>Your use of the Service is also governed by our Privacy Policy. Please review it to understand how we collect, use, and protect your personal information.</p>

<h3>16. Termination</h3>
<p>We may suspend or terminate your access to the Service at our sole discretion, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.</p>

<h3>17. Governing Law &amp; Dispute Resolution</h3>
<p>These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which <strong>{{COMPANY}}</strong> is established, without regard to conflict-of-law principles. Any dispute arising from these Terms shall first be attempted to be resolved through good-faith negotiation; failing which, it shall be submitted to the competent courts of that jurisdiction.</p>

<h3>18. Severability</h3>
<p>If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.</p>

<h3>19. Changes to These Terms</h3>
<p>We reserve the right to modify these Terms at any time. Material changes will be communicated through the Service or via email. Your continued use after changes constitutes acceptance.</p>

<h3>20. Contact Us</h3>
<p>If you have questions about these Terms, please contact us at <strong>{{EMAIL}}</strong>.</p>
`.trim();

// ── Privacy Policy ──────────────────────────────────────────────────────

export const PRIVACY_POLICY = `
<h2>Privacy Policy</h2>
<p><strong>Effective date:</strong> {{DATE}}</p>
<p><strong>{{COMPANY}}</strong> (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your privacy. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you visit our website, use our applications, or interact with our services (collectively, the &ldquo;Service&rdquo;).</p>

<h3>1. Information We Collect</h3>
<h4>a. Information You Provide</h4>
<ul>
  <li><strong>Account data:</strong> name, email address, phone number, billing/shipping address;</li>
  <li><strong>Payment data:</strong> payment card details are collected by our payment processor &mdash; we do not store full card numbers;</li>
  <li><strong>Communications:</strong> messages you send us via contact forms, email, or support chat;</li>
  <li><strong>User content:</strong> reviews, comments, or any content you voluntarily submit.</li>
</ul>
<h4>b. Information Collected Automatically</h4>
<ul>
  <li><strong>Device &amp; browser data:</strong> IP address, browser type, operating system, device identifiers;</li>
  <li><strong>Usage data:</strong> pages visited, time spent, click paths, referral URLs;</li>
  <li><strong>Cookies &amp; similar technologies:</strong> we use cookies, pixels, and local storage to enhance your experience, analyse usage, and serve relevant content. See Section 7 below.</li>
</ul>
<h4>c. Information From Third Parties</h4>
<p>We may receive information from payment processors, analytics providers, advertising partners, and public databases as permitted by law.</p>

<h3>2. How We Use Your Information</h3>
<p>We use collected information to:</p>
<ul>
  <li>Provide, operate, and maintain the Service;</li>
  <li>Process transactions and send related notifications;</li>
  <li>Personalise your experience and deliver relevant content;</li>
  <li>Communicate with you &mdash; respond to inquiries, send updates, and marketing communications (with your consent where required);</li>
  <li>Detect, prevent, and address fraud, abuse, and security issues;</li>
  <li>Comply with legal obligations and enforce our terms;</li>
  <li>Analyse usage trends and improve the Service.</li>
</ul>

<h3>3. Legal Basis for Processing (GDPR / UK GDPR)</h3>
<p>Where applicable, we process your data based on:</p>
<ul>
  <li><strong>Consent:</strong> you have given clear consent (e.g., marketing emails);</li>
  <li><strong>Contract:</strong> processing is necessary to fulfil our obligations to you;</li>
  <li><strong>Legitimate interest:</strong> processing is in our legitimate business interest and not overridden by your rights;</li>
  <li><strong>Legal obligation:</strong> processing is necessary to comply with applicable law.</li>
</ul>

<h3>4. How We Share Your Information</h3>
<p>We do <strong>not</strong> sell your personal information. We may share data with:</p>
<ul>
  <li><strong>Service providers:</strong> hosting, payment processing, analytics, email delivery, and customer support vendors who process data on our behalf;</li>
  <li><strong>Legal authorities:</strong> when required by law, regulation, legal process, or government request;</li>
  <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets;</li>
  <li><strong>With your consent:</strong> for any other purpose disclosed at the time of collection.</li>
</ul>

<h3>5. International Data Transfers</h3>
<p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) or equivalent mechanisms recognised by applicable data protection authorities.</p>

<h3>6. Data Retention</h3>
<p>We retain your personal data only for as long as necessary to fulfil the purposes described in this Policy, comply with legal obligations, resolve disputes, and enforce agreements. When data is no longer needed, it is securely deleted or anonymised.</p>

<h3>7. Cookies &amp; Tracking Technologies</h3>
<p>We use:</p>
<ul>
  <li><strong>Essential cookies:</strong> necessary for the Service to function;</li>
  <li><strong>Analytics cookies:</strong> to understand how visitors use the Service;</li>
  <li><strong>Marketing cookies:</strong> to deliver relevant advertisements.</li>
</ul>
<p>You can manage cookie preferences through your browser settings or our cookie consent banner. Disabling certain cookies may affect functionality.</p>

<h3>8. Your Rights</h3>
<p>Depending on your jurisdiction, you may have the right to:</p>
<ul>
  <li><strong>Access:</strong> request a copy of the personal data we hold about you;</li>
  <li><strong>Rectification:</strong> request correction of inaccurate or incomplete data;</li>
  <li><strong>Erasure:</strong> request deletion of your personal data (&ldquo;right to be forgotten&rdquo;);</li>
  <li><strong>Restriction:</strong> request that we limit processing of your data;</li>
  <li><strong>Portability:</strong> receive your data in a structured, machine-readable format;</li>
  <li><strong>Objection:</strong> object to processing based on legitimate interests or direct marketing;</li>
  <li><strong>Withdraw consent:</strong> withdraw consent at any time where processing is consent-based;</li>
  <li><strong>Lodge a complaint:</strong> file a complaint with your local data protection authority.</li>
</ul>
<p>To exercise any of these rights, please contact us at <strong>{{EMAIL}}</strong>. We will respond within the timeframe required by applicable law.</p>

<h3>9. California Residents (CCPA / CPRA)</h3>
<p>If you are a California resident, you have additional rights under the California Consumer Privacy Act, including the right to know, delete, and opt out of the &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal information. We do not sell personal information. For requests, contact us at <strong>{{EMAIL}}</strong>.</p>

<h3>10. Children&rsquo;s Privacy</h3>
<p>The Service is not directed to individuals under 16 (or the applicable age in your jurisdiction). We do not knowingly collect personal data from children. If we become aware that we have collected data from a child, we will promptly delete it.</p>

<h3>11. Security</h3>
<p>We implement industry-standard technical and organisational measures to protect your data, including encryption in transit (TLS), access controls, and regular security assessments. However, no method of transmission over the internet is 100% secure.</p>

<h3>12. Third-Party Links</h3>
<p>The Service may contain links to third-party websites. We are not responsible for the privacy practices of those sites. We encourage you to read their privacy policies.</p>

<h3>13. Changes to This Policy</h3>
<p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on the Service and updating the effective date. Your continued use after changes constitutes acceptance.</p>

<h3>14. Contact Us</h3>
<p>If you have questions or concerns about this Privacy Policy, please contact us at <strong>{{EMAIL}}</strong>.</p>
`.trim();

// ── Return & Refund Policy ──────────────────────────────────────────────

export const RETURN_POLICY = `
<h2>Return &amp; Refund Policy</h2>
<p><strong>Effective date:</strong> {{DATE}}</p>
<p>Thank you for shopping with <strong>{{COMPANY}}</strong>. We want you to be completely satisfied with your purchase. This policy outlines the terms under which you may return products and request refunds.</p>

<h3>1. Return Eligibility</h3>
<ul>
  <li>Items may be returned within <strong>30 days</strong> of the delivery date;</li>
  <li>Products must be unused, in original packaging, and in the same condition as received;</li>
  <li>Proof of purchase (order number or receipt) is required;</li>
  <li>Items marked as &ldquo;Final Sale&rdquo; or &ldquo;Non-Returnable&rdquo; on the product page cannot be returned.</li>
</ul>

<h3>2. Non-Returnable Items</h3>
<p>The following items generally cannot be returned unless defective or damaged on arrival:</p>
<ul>
  <li>Perishable goods (food, flowers, etc.);</li>
  <li>Custom-made or personalised products;</li>
  <li>Digital products, downloads, and software licences;</li>
  <li>Gift cards and vouchers;</li>
  <li>Intimate or hygiene-sensitive products (underwear, swimwear, beauty items that have been opened);</li>
  <li>Hazardous materials or flammable liquids.</li>
</ul>

<h3>3. How to Initiate a Return</h3>
<ol>
  <li>Contact us at <strong>{{EMAIL}}</strong> with your order number and reason for the return;</li>
  <li>We will provide a Return Merchandise Authorisation (RMA) number and shipping instructions;</li>
  <li>Pack the item securely in its original packaging and include the RMA number;</li>
  <li>Ship the item to the address provided. We recommend using a trackable shipping service.</li>
</ol>

<h3>4. Return Shipping Costs</h3>
<ul>
  <li><strong>Defective, damaged, or wrong items:</strong> we will cover return shipping costs or arrange a prepaid label;</li>
  <li><strong>Change of mind returns:</strong> return shipping costs are the responsibility of the customer;</li>
  <li>Original shipping charges are non-refundable unless the return is due to our error.</li>
</ul>

<h3>5. Inspection &amp; Refund Processing</h3>
<ul>
  <li>Once we receive the returned item, we will inspect it within <strong>5 business days</strong>;</li>
  <li>If approved, your refund will be processed to the original payment method within <strong>5&ndash;10 business days</strong>;</li>
  <li>Depending on your bank or card issuer, it may take an additional 3&ndash;7 business days for the refund to appear in your account;</li>
  <li>If the item is returned in a condition that does not meet our return requirements, we reserve the right to decline the refund or issue a partial refund.</li>
</ul>

<h3>6. Exchanges</h3>
<p>If you wish to exchange an item for a different size, colour, or variant, please contact us at <strong>{{EMAIL}}</strong>. Exchanges are subject to availability. If the replacement is not available, we will process a refund instead.</p>

<h3>7. Damaged or Defective Items</h3>
<p>If you receive a damaged or defective item, please contact us within <strong>48 hours</strong> of delivery with photographs of the damage. We will arrange a replacement or full refund at no additional cost to you.</p>

<h3>8. Late or Missing Refunds</h3>
<p>If you have not received your refund after the stated timeframe:</p>
<ol>
  <li>Check your bank or credit card account again;</li>
  <li>Contact your bank or card issuer, as processing times may vary;</li>
  <li>If you have done both and still have not received your refund, contact us at <strong>{{EMAIL}}</strong>.</li>
</ol>

<h3>9. Cancellations</h3>
<p>Orders may be cancelled before they are shipped. Once an order has been dispatched, it cannot be cancelled but may be returned after delivery per the terms above.</p>

<h3>10. Digital Products &amp; Services</h3>
<p>For digital products, subscriptions, or services: refunds are available within <strong>14 days</strong> of purchase if you have not substantially used or downloaded the product. Subscription fees already charged for a used period are non-refundable.</p>

<h3>11. Consumer Rights</h3>
<p>This policy does not affect your statutory rights under applicable consumer protection laws, including the EU Consumer Rights Directive, the UK Consumer Rights Act, or any other local legislation that provides additional protections.</p>

<h3>12. Changes to This Policy</h3>
<p>We may update this Return &amp; Refund Policy from time to time. Changes will be posted on this page with an updated effective date.</p>

<h3>13. Contact Us</h3>
<p>For any return or refund enquiries, please contact us at <strong>{{EMAIL}}</strong>.</p>
`.trim();

// ── Template resolver ───────────────────────────────────────────────────

export function resolveTemplate(
  template: string,
  projectName: string,
  contactEmail?: string
): string {
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const email = contactEmail || `support@${projectName.toLowerCase().replace(/\s+/g, '')}.com`;
  return template
    .replace(/\{\{COMPANY\}\}/g, projectName)
    .replace(/\{\{DATE\}\}/g, date)
    .replace(/\{\{EMAIL\}\}/g, email);
}

export const DEFAULT_TEMPLATES: Record<string, string> = {
  termsAndConditions: TERMS_AND_CONDITIONS,
  privacyPolicy: PRIVACY_POLICY,
  returnPolicy: RETURN_POLICY,
};
